/**
 * Connection status.
 *
 * ## Why this is not a decoration
 *
 * A realtime board has a failure mode with no visible symptom: the socket dies,
 * every other participant keeps working, and this user's board quietly stops
 * updating. They drag a card, it moves locally, nobody else sees it. Without an
 * indicator the user has no way to distinguish "nothing is happening" from "I am
 * not connected", and the two call for completely different responses.
 *
 * ## Why the four states are distinct
 *
 * - `connected` - the handshake completed. Shows round-trip time, which is the
 *   only continuous evidence that the socket is alive rather than merely open.
 * - `connecting` - the first attempt. Distinct from `reconnecting` because a
 *   user who has just opened a board should not be told something is wrong.
 * - `reconnecting` - a connection was lost and is being retried. Names the
 *   condition, because the board still works (mutations fall back to HTTP) and
 *   the user should know that rather than stopping.
 * - `offline` - terminal, or the browser reports no network. Says so plainly.
 *
 * The pill is a `role="status"` region so a transition is announced. The
 * round-trip number is `aria-hidden`: it changes constantly and announcing it
 * would flood a screen reader.
 */

import type { ReactNode } from 'react';
import type { ConnectionStatus } from '@slate/shared';
import { useBoard } from '../state/BoardProvider.js';

const LABEL: Record<ConnectionStatus, string> = {
  connected: 'Live',
  connecting: 'Connecting',
  reconnecting: 'Reconnecting',
  offline: 'Offline',
};

const DOT_CLASS: Record<ConnectionStatus, string> = {
  connected: 'bg-success',
  connecting: 'bg-warning',
  reconnecting: 'bg-warning animate-pulse',
  offline: 'bg-danger',
};

/** Latency above this is worth showing in a warning colour. 250 ms is roughly
 * where a drag feels like it is fighting the user. */
const SLOW_RTT_MS = 250;

export function ConnectionBadge(): ReactNode {
  const { state } = useBoard();
  const { connection, latencyMs } = state;

  const detail =
    connection === 'connected'
      ? latencyMs === null
        ? null
        : `${latencyMs} ms`
      : connection === 'offline'
        ? 'Changes are not being saved'
        : null;

  return (
    <div
      role="status"
      aria-live="polite"
      title={detail ?? LABEL[connection]}
      className="flex items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-ink-muted"
    >
      <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${DOT_CLASS[connection]}`} />
      <span className="font-medium">{LABEL[connection]}</span>
      {connection === 'connected' && latencyMs !== null && (
        <span
          aria-hidden="true"
          className={`tabular-nums ${latencyMs > SLOW_RTT_MS ? 'text-warning' : 'text-ink-subtle'}`}
        >
          {latencyMs} ms
        </span>
      )}
    </div>
  );
}
